import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/use-toast';
import { saveCostSheetColorPresets } from '@/lib/api/manufacturing';
import { initialColorDetails, serializeColorPreset, colorPresetEditorSheet } from '@/lib/costSheetColorPresets';
import { nextVariantKey } from '@/lib/materialColors';
import { MaterialColorMatrix } from './MaterialColorMatrix';
import { SimulationSizeColorMatrix } from './SimulationSizeColorMatrix';

export function CostSheetColorPresetDialog(props) {
  return props.open ? <ColorPresetEditor key={props.sheet?.id} {...props} /> : null;
}

function ColorPresetEditor({ sheet, open, onOpenChange, onSaved, readOnly = false }) {
  const [draft, setDraft] = React.useState(() => initialColorDetails(sheet));
  const [excludedIds, setExcludedIds] = React.useState([]);
  const editorSheet = colorPresetEditorSheet(sheet, excludedIds);
  const [saving, setSaving] = React.useState(false);
  const savingRef = React.useRef(false);
  const { toast } = useToast();
  const revision = React.useRef(sheet?.color_preset?.revision || 0);
  const save = async () => {
    if (savingRef.current || readOnly) return;
    savingRef.current = true; setSaving(true);
    try {
      const result = await saveCostSheetColorPresets(sheet.id,
        serializeColorPreset(editorSheet, draft.simulated_colors, draft.color_variants), revision.current, excludedIds);
      if (result.error) throw result.error;
      await onSaved?.(result.data);
      onOpenChange(false);
      toast({ title: 'Variantes del escandallo guardadas' });
    } catch (error) { toast({ title: 'No se pudieron guardar las variantes', description: error.message, variant: 'destructive' }); }
    finally { savingRef.current = false; setSaving(false); }
  };
  return <Dialog open={open} onOpenChange={(value) => !savingRef.current && onOpenChange(value)}>
    <DialogContent className="w-[95vw] max-w-7xl max-h-[90vh] overflow-y-auto">
      <DialogHeader><DialogTitle>Variantes de color del escandallo</DialogTitle>
        <DialogDescription>Opcional. Al añadir este artículo a una nueva simulación u orden se cargarán estos colores, sin cantidades. Después podrás modificarlos en cada documento.</DialogDescription>
      </DialogHeader>
      <fieldset disabled={saving || readOnly} className="min-w-0 space-y-4">
        <SimulationSizeColorMatrix colorsOnly allowVariants hideContrasts readOnly={readOnly}
          colors={draft.simulated_colors} variants={draft.color_variants}
          onAddColor={(base) => setDraft((current) => {
            const color = nextVariantKey(base, current.simulated_colors);
            return { simulated_colors: [...current.simulated_colors, color], color_variants: { ...current.color_variants,
              [color]: { variant_id: crypto.randomUUID(), garment_color: base, material_colors: {} } } };
          })}
          onRemoveColor={(color) => setDraft((current) => ({ simulated_colors: current.simulated_colors.filter((item) => item !== color),
            color_variants: Object.fromEntries(Object.entries(current.color_variants).filter(([key]) => key !== color)) }))} />
        <MaterialColorMatrix sheet={editorSheet} presetEditor
          onExclude={readOnly ? undefined : (id) => setExcludedIds(current => [...current, id])} colors={draft.simulated_colors} variants={draft.color_variants}
          onChange={(color_variants) => setDraft((current) => ({ ...current, color_variants }))} />
      </fieldset>
      {!!excludedIds.length && <p className="text-sm text-gray-600">Las columnas ocultas se guardarán al pulsar Guardar variantes. Para recuperarlas después, edita el material de este artículo.</p>}
      <DialogFooter><Button type="button" variant="outline" disabled={saving} onClick={() => onOpenChange(false)}>{readOnly ? 'Cerrar' : 'Cancelar'}</Button>
        {!readOnly && <Button type="button" disabled={saving} onClick={save}>{saving ? 'Guardando…' : 'Guardar variantes'}</Button>}
      </DialogFooter>
    </DialogContent>
  </Dialog>;
}
